/**
 * cachePolicyManager.js — Centralized cache policy for both TanStack Query and Service Worker
 * Manages dynamic TTL values that admin can change
 * Both systems subscribe to updates and apply new policies
 */

// Default TTL values (same as in AdminPanel.js)
export const DEFAULT_CACHE_POLICY = {
  reference_data_ttl_seconds: 600,        // 10 min - branches, doctors, users, salespersons
  dashboard_reports_ttl_seconds: 180,     // 3 min - dashboard summary
  monthly_report_ttl_seconds: 300,        // 5 min - monthly reports
  paged_records_ttl_seconds: 60,          // 1 min - paginated lists
  notify_on_stale_data: true,
};

// Current policy in memory
let currentCachePolicy = { ...DEFAULT_CACHE_POLICY };

// Subscribers for policy changes
let policySubscribers = [];

/**
 * Get current cache policy
 * @returns {object} Current cache policy
 */
export const getCachePolicy = () => currentCachePolicy;

/**
 * Update cache policy (called when admin changes settings or on app init)
 * @param {object} newPolicy - New cache policy object
 */
export const updateCachePolicy = (newPolicy) => {
  if (!newPolicy) return;

  // Merge with existing policy (don't lose unset values)
  currentCachePolicy = {
    ...DEFAULT_CACHE_POLICY,
    ...newPolicy,
  };

  if (typeof window !== 'undefined' && window.isDev) {
    console.log('[CachePolicy] Updated:', currentCachePolicy);
  }

  // Notify all subscribers
  notifySubscribers();
};

/**
 * Subscribe to policy changes
 * Called when policy is updated remotely or via admin
 * @param {Function} callback - Called with updated policy
 * @returns {Function} Unsubscribe function
 */
export const onPolicyChange = (callback) => {
  policySubscribers.push(callback);
  // Immediately call with current policy
  callback(currentCachePolicy);
  
  return () => {
    policySubscribers = policySubscribers.filter((cb) => cb !== callback);
  };
};

/**
 * Notify all subscribers of policy change
 */
const notifySubscribers = () => {
  policySubscribers.forEach((callback) => {
    try {
      callback(currentCachePolicy);
    } catch (error) {
      console.error('[CachePolicy] Subscriber error:', error);
    }
  });
};

/**
 * Calculate staleTime in milliseconds based on endpoint/resource type
 * Used by TanStack Query to determine when data becomes stale
 * @param {string} resourceType - Type of resource ('patient', 'product', 'dashboard', etc)
 * @returns {number} staleTime in milliseconds
 */
export const getStaleTimeForResource = (resourceType) => {
  let ttlSeconds;

  switch (resourceType) {
    // Reference data: rarely changes, longer TTL
    case 'branches':
    case 'doctors':
    case 'salespersons':
    case 'users':
      ttlSeconds = currentCachePolicy.reference_data_ttl_seconds;
      break;

    // Dashboard metrics: changes frequently, short TTL
    case 'dashboard':
    case 'dashboard_summary':
      ttlSeconds = currentCachePolicy.dashboard_reports_ttl_seconds;
      break;

    // Monthly reports: moderate changes, moderate TTL
    case 'monthly_report':
    case 'monthly_reports':
      ttlSeconds = currentCachePolicy.monthly_report_ttl_seconds;
      break;

    // Paged records: frequent updates, shortest TTL
    case 'patients':
    case 'products':
    case 'invoices':
    case 'services':
    case 'audiometry':
      ttlSeconds = currentCachePolicy.paged_records_ttl_seconds;
      break;

    // Default: 1 hour
    default:
      ttlSeconds = 60 * 60;
  }

  // Convert seconds to milliseconds
  return ttlSeconds * 1000;
};

/**
 * Calculate staleTime for an endpoint pathname
 * Used by Service Worker routing for consistency
 * @param {string} pathname - URL pathname
 * @returns {number} staleTime in seconds
 */
export const getStaleTimeForEndpoint = (pathname) => {
  // Reference data endpoints
  if (/\/(get-(branch|doctor|user|salesperson)-list)/.test(pathname)) {
    return currentCachePolicy.reference_data_ttl_seconds;
  }

  // Dashboard reports
  if (/\/(reports\/dashboard-summary)/.test(pathname)) {
    return currentCachePolicy.dashboard_reports_ttl_seconds;
  }

  // Monthly reports
  if (/\/(get-invoices-for-month-report)/.test(pathname)) {
    return currentCachePolicy.monthly_report_ttl_seconds;
  }

  // Paged records
  if (/\/(get-(patient|product|invoice|service|audiometry)-list-paged)/.test(pathname)) {
    return currentCachePolicy.paged_records_ttl_seconds;
  }

  // Default to 1 hour
  return 60 * 60;
};

/**
 * Load cache policy from localStorage (set by Service Worker)
 * Fallback to defaults if not available
 * @returns {object} Cache policy
 */
export const loadCachePolicyFromStorage = () => {
  try {
    const stored = localStorage.getItem('happyears-cache-policy');
    if (stored) {
      const policy = JSON.parse(stored);
      updateCachePolicy(policy);
      return policy;
    }
  } catch (error) {
    console.warn('[CachePolicy] Failed to load from localStorage:', error);
  }
  return currentCachePolicy;
};

/**
 * Listen for cache policy updates from Service Worker or SSE
 * Called when admin changes TTL settings
 */
export const setupPolicyUpdateListener = () => {
  // Listen for SERVICE WORKER updates
  if (navigator.serviceWorker?.controller) {
    const onMessage = (event) => {
      if (event.data?.type === 'CACHE_POLICY_UPDATED') {
        console.log('[CachePolicy] Update from Service Worker:', event.data.cachePolicy);
        updateCachePolicy(event.data.cachePolicy);
      }
    };

    navigator.serviceWorker.controller.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.controller?.removeEventListener('message', onMessage);
    };
  }
};

/**
 * Broadcast policy update to Service Worker
 * Called when receiving policy from backend
 * @param {object} policy - Cache policy to broadcast
 */
export const broadcastPolicyToServiceWorker = async (policy) => {
  if (!navigator.serviceWorker?.controller) return;

  return new Promise((resolve) => {
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      console.log('[CachePolicy] Service Worker acknowledged update');
      resolve(true);
    };

    navigator.serviceWorker.controller.postMessage(
      {
        type: 'UPDATE_CACHE_POLICY',
        cachePolicy: policy,
      },
      [channel.port2]
    );

    setTimeout(() => resolve(true), 2000);
  });
};
